import React from 'react'
import clsx from 'clsx'

import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import { IconButton, Paper, Typography, Skeleton, Tooltip } from '@mui/material'
import { useOnClickOutside } from 'usehooks-ts'
interface PrimSecTextComboProps {
  primaryText: string | number | React.ReactNode
  secondaryText: string | number
  variant?: 'default' | 'inverted'
  primaryTextClassName?: string
  /** Set `collapsible` to `true` if you need an
   * expandable view for the primary text.
   * For it to work, the parent container
   * MUST have it's `position` set to `relative`*/
  collapsible?: boolean
  classes?: {
    primaryText?: string
    secondaryText?: string
    root?: string
  }
  tooltip?: string
}

const CollapsibleWrapper: React.FC<PrimSecTextComboProps> = ({
  primaryText,
  secondaryText,
  variant = 'default',
  primaryTextClassName = '',
  collapsible = false,
  tooltip,
  classes,
}) => {
  const primaryTextStr = primaryText ? primaryText.toString() : ''
  const primaryTextLength = primaryTextStr.length
  const [expanded, setExpanded] = React.useState(false)
  const divRef = React.useRef<HTMLDivElement>(null)

  const outsideAlertToggle = () => {
    setExpanded(false)
  }
  useOnClickOutside(divRef, outsideAlertToggle)

  const truncatedPrimaryText = primaryTextStr.slice(0, 60)

  if (!collapsible)
    return (
      <PrimSecTextCombo
        primaryText={primaryText}
        secondaryText={secondaryText}
        variant={variant}
        primaryTextClassName={primaryTextClassName}
        classes={classes}
        tooltip={tooltip}
      />
    )

  return expanded ? (
    <Paper
      className={clsx(
        'absolute top-0 z-[1] -ml-2 flex w-full items-center justify-between p-2',
        classes?.root
      )}
      ref={divRef}
    >
      <PrimSecTextCombo
        primaryText={primaryText}
        secondaryText={secondaryText}
        variant={variant}
        primaryTextClassName={primaryTextClassName}
        tooltip={tooltip}
      />
      <IconButton
        onClick={(event) => {
          event.stopPropagation()
          setExpanded(!expanded)
        }}
      >
        <OpenInFullIcon />
      </IconButton>
    </Paper>
  ) : (
    <div className={clsx('w-full items-center justify-between', classes?.root)}>
      <div className="flex flex-col gap-1">
        <div className="flex items-center">
          <PrimSecTextCombo
            primaryText={
              primaryTextLength > 60
                ? `${truncatedPrimaryText}...`
                : primaryTextStr
            }
            tooltip={tooltip}
            secondaryText={secondaryText}
            variant={variant}
            primaryTextClassName={primaryTextClassName}
          />
          {primaryTextLength > 60 && (
            <IconButton
              onClick={(event) => {
                event.stopPropagation()
                setExpanded(!expanded)
              }}
            >
              <OpenInFullIcon />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  )
}

const PrimSecTextCombo: React.FC<
  Omit<PrimSecTextComboProps, 'collapsible'>
> = ({
  primaryText,
  secondaryText,
  tooltip,
  variant = 'default',
  primaryTextClassName = '',
  classes = {},
}) => {
  return (
    <Tooltip title={tooltip}>
      <div className={clsx(classes?.root)}>
        {variant === 'default' ? (
          <>
            <Typography
              variant="body2"
              className={clsx(primaryTextClassName, classes?.primaryText)}
            >
              {primaryText}
            </Typography>
            <Typography variant="caption" className={classes?.secondaryText}>
              {secondaryText !== undefined ? (
                secondaryText
              ) : (
                <Skeleton width={75} />
              )}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="caption" className={classes?.secondaryText}>
              {secondaryText}
            </Typography>
            <Typography
              variant="body2"
              className={clsx(primaryTextClassName, classes?.primaryText)}
            >
              {primaryText !== undefined ? (
                primaryText
              ) : (
                <Skeleton width={75} />
              )}
            </Typography>
          </>
        )}
      </div>
    </Tooltip>
  )
}

export default CollapsibleWrapper
